import type { ReactNode } from 'react';

import { Leads_Property_Usage_Enum } from '@graphql/__generated__/graphql';
import {
  formatIncome,
  getRatingLabel,
} from '@modules/_shared/utils/formatting';
import {
  translatePropertyOccupied,
  translatePropertyUsage,
} from '@modules/_shared/utils/lead-labels';
import {
  extractPropertyOrientations,
  getEnergyClassValues,
  getOrientationTranslation,
  getPropertyCoolingLabel,
  getPropertyEquipmentFeatures,
  getPropertyHeatingDistributionLabel,
  getPropertyHeatingLabel,
  getPropertySides,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import type { LeadDetailsDrawer } from '@modules/lead-details/type';
import type { Translate } from '@shared/hooks/locale';
import {
  type IntlLocale,
  getCurrencyByCountryCode,
} from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export type PropertyItem = {
  label: string;
  value: string | ReactNode;
  showByDefault: boolean;
};

type Property = LeadDetailsDrawer['property'];

export const mapBasicPropertyItems = (
  property: Property,
  lead: LeadDetailsDrawer,
  t: Translate,
  isSpain: boolean,
  locale: IntlLocale,
) => {
  const items: PropertyItem[] = [];
  const currency = getCurrencyByCountryCode(property?.country_code ?? 'CH');

  if (lead.appraisal_perceived_value) {
    items.push({
      label: t('Perceived value'),
      value: formatPrice(lead.appraisal_perceived_value, locale, currency),
      showByDefault: true,
    });
  }

  if (lead.appraisal_property_occupied) {
    items.push({
      label: t('Property occupancy'),
      value: translatePropertyOccupied(t, lead.appraisal_property_occupied),
      showByDefault: true,
    });
  }

  if (
    lead.property_usage &&
    lead.property_usage !== Leads_Property_Usage_Enum.NotSet
  ) {
    items.push({
      label: t('Property usage'),
      value: translatePropertyUsage(t, lead.property_usage),
      showByDefault: true,
    });
  }

  if (property?.property_type?.label) {
    items.push({
      label: t('Property type'),
      value: property.property_type.label,
      showByDefault: true,
    });
  }

  if (isSpain && property?.number_of_bedrooms) {
    items.push({
      label: t('Number of bedrooms'),
      value: `${property.number_of_bedrooms}`,
      showByDefault: true,
    });
  } else if (property?.number_of_rooms) {
    items.push({
      label: t('Rooms'),
      value: `${property.number_of_rooms}`,
      showByDefault: true,
    });
  }

  if (isSpain && property?.built_surface) {
    items.push({
      label: t('Built surface'),
      value: `${property.built_surface} m²`,
      showByDefault: true,
    });
  } else if (property?.living_surface) {
    items.push({
      label: t('Living surface'),
      value: `${property.living_surface} m²`,
      showByDefault: true,
    });
  }

  return items;
};

export const mapLandSurfaceItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];
  const isApartment = property?.property_type?.main_type === 'APPT';

  if (!isApartment && property?.land_surface) {
    items.push({
      label: t('Land surface'),
      value: `${property.land_surface} m²`,
      showByDefault: true,
    });
  }

  return items;
};

export const mapPlotNumberItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.plot_number) {
    items.push({
      label: t('plotNumberLabel'),
      value: property.plot_number,
      showByDefault: false,
    });
  }

  return items;
};

export const mapSurfaceItems = (
  property: Property,
  t: Translate,
  isSpain: boolean,
) => {
  const items: PropertyItem[] = [];

  if (!isSpain && property?.built_surface) {
    items.push({
      label: t('Built surface'),
      value: `${property.built_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.usable_surface) {
    items.push({
      label: t('Usable surface'),
      value: `${property.usable_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.gross_floor_surface) {
    items.push({
      label: t('Gross floor surface'),
      value: `${property.gross_floor_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.balcony_surface) {
    items.push({
      label: t('Balcony surface'),
      value: `${property.balcony_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.terrace_surface) {
    items.push({
      label: t('Terrace surface'),
      value: `${property.terrace_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.garden_surface) {
    items.push({
      label: t('Garden surface'),
      value: `${property.garden_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.basement_surface) {
    items.push({
      label: t('Basement surface'),
      value: `${property.basement_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.weighted_floor_surface) {
    items.push({
      label: t('Weighted floor surface'),
      value: `${property.weighted_floor_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.building_volume) {
    items.push({
      label: t('Building volume'),
      value: `${property.building_volume} m³`,
      showByDefault: false,
    });
  }

  if (property?.residential_surface) {
    items.push({
      label: t('Residential surface'),
      value: `${property.residential_surface} m²`,
      showByDefault: false,
    });
  }

  if (property?.commercial_surface) {
    items.push({
      label: t('Commercial surface'),
      value: `${property.commercial_surface} m²`,
      showByDefault: false,
    });
  }

  return items;
};

export const mapRoomCountItems = (
  property: Property,
  t: Translate,
  isSpain: boolean,
) => {
  const items: PropertyItem[] = [];

  if (!isSpain && property?.number_of_bedrooms) {
    items.push({
      label: t('Number of bedrooms'),
      value: `${property.number_of_bedrooms}`,
      showByDefault: false,
    });
  }

  if (property?.number_of_bathrooms) {
    items.push({
      label: t('Number of bathrooms'),
      value: `${property.number_of_bathrooms}`,
      showByDefault: false,
    });
  }

  if (property?.number_of_toilets) {
    items.push({
      label: t('Number of toilets'),
      value: `${property.number_of_toilets}`,
      showByDefault: false,
    });
  }

  return items;
};

export const mapUnitItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.number_of_residential_units) {
    items.push({
      label: t('numberOfResidentialUnits'),
      value: `${property.number_of_residential_units}`,
      showByDefault: false,
    });
  }

  if (property?.number_of_commercial_units) {
    items.push({
      label: t('numberOfCommercialUnits'),
      value: `${property.number_of_commercial_units}`,
      showByDefault: false,
    });
  }

  return items;
};

export const mapFloorItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.number_of_floors) {
    items.push({
      label: t('numberOfFloors'),
      value: `${property.number_of_floors}`,
      showByDefault: false,
    });
  }

  if (property?.floor_of_flat) {
    items.push({
      label: t('floorOfFlat'),
      value: `${property.floor_of_flat}`,
      showByDefault: false,
    });
  }

  if (property?.number_of_lifts) {
    items.push({
      label: t('Number of elevators'),
      value: `${property.number_of_lifts}`,
      showByDefault: false,
    });
  }

  if (property?.side) {
    const sideLabel =
      getPropertySides(t).find(s => s.value === property.side)?.label ??
      property.side;

    items.push({
      label: t('side'),
      value: sideLabel,
      showByDefault: false,
    });
  }

  return items;
};

export const mapOrientationItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property) {
    const orientations = extractPropertyOrientations(property);
    if (orientations.length > 0) {
      const translatedOrientations = orientations
        .map(o => getOrientationTranslation(t, o))
        .filter(o => o !== '-');

      if (translatedOrientations.length > 0) {
        items.push({
          label: t('Orientation'),
          value: translatedOrientations.join(', '),
          showByDefault: false,
        });
      }
    }
  }

  return items;
};

export const mapParkingItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.number_of_indoor_parkings) {
    items.push({
      label: t('indoorParking'),
      value: `${property.number_of_indoor_parkings}`,
      showByDefault: false,
    });
  }

  if (property?.number_of_outdoor_parkings) {
    items.push({
      label: t('outdoorParking'),
      value: `${property.number_of_outdoor_parkings}`,
      showByDefault: false,
    });
  }

  return items;
};

export const mapHVACItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.heating) {
    items.push({
      label: t('Heating'),
      value: getPropertyHeatingLabel(property.heating, t) ?? property.heating,
      showByDefault: false,
    });
  }

  if (property?.heating_distribution) {
    items.push({
      label: t('Heating distribution'),
      value:
        getPropertyHeatingDistributionLabel(property.heating_distribution, t) ??
        property.heating_distribution,
      showByDefault: false,
    });
  }

  if (property?.cooling) {
    items.push({
      label: t('Cooling'),
      value: getPropertyCoolingLabel(property.cooling, t) ?? property.cooling,
      showByDefault: false,
    });
  }

  return items;
};

export const mapEnergyItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.energy_consumption_class) {
    const energyConsumptionClassLabel =
      getEnergyClassValues(t).find(
        e => e.value === property.energy_consumption_class,
      )?.label ?? property.energy_consumption_class;

    items.push({
      label: t('energyConsumptionClass'),
      value: energyConsumptionClassLabel,
      showByDefault: false,
    });
  }

  if (property?.gas_emissions_class) {
    const gasEmissionsClassLabel =
      getEnergyClassValues(t).find(
        e => e.value === property.gas_emissions_class,
      )?.label ?? property.gas_emissions_class;

    items.push({
      label: t('gasEmissionsClass'),
      value: gasEmissionsClassLabel,
      showByDefault: false,
    });
  }

  if (property?.consumption) {
    items.push({
      label: t('consumption'),
      value: `${property.consumption} kWh/m²/y`,
      showByDefault: false,
    });
  }

  if (property?.emissions) {
    items.push({
      label: t('emissions'),
      value: `${property.emissions} kg CO2/m²/y`,
      showByDefault: false,
    });
  }

  return items;
};

export const mapConstructionItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.construction_year) {
    items.push({
      label: t('Construction year'),
      value: `${property.construction_year}`,
      showByDefault: false,
    });
  }

  if (property?.renovation_year) {
    items.push({
      label: t('Renovation year'),
      value: `${property.renovation_year}`,
      showByDefault: false,
    });
  }

  if (property?.building_standard) {
    items.push({
      label: t('buildingStandard'),
      value: getRatingLabel(property.building_standard, t) ?? '',
      showByDefault: false,
    });
  }

  if (property?.building_condition) {
    items.push({
      label: t('buildingCondition'),
      value: getRatingLabel(property.building_condition, t) ?? '',
      showByDefault: false,
    });
  }

  if (property?.minergie_code) {
    items.push({
      label: t('minergieCode'),
      value: `${property.minergie_code}`,
      showByDefault: false,
    });
  }

  return items;
};

export const mapLocationItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.micro_location) {
    items.push({
      label: t('microLocation'),
      value: getRatingLabel(property.micro_location, t) ?? '',
      showByDefault: false,
    });
  }

  if (property?.macro_location) {
    items.push({
      label: t('macroLocation'),
      value: getRatingLabel(property.macro_location, t) ?? '',
      showByDefault: false,
    });
  }

  return items;
};

export const mapRentalIncomeItems = (
  property: Property,
  t: Translate,
  locale: IntlLocale,
) => {
  const items: PropertyItem[] = [];
  const currency = getCurrencyByCountryCode(property?.country_code ?? 'CH');

  if (property?.residential_yearly_rental_income) {
    items.push({
      label: t('actualRentalIncomeResidential'),
      value: formatIncome(
        property.residential_yearly_rental_income,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.residential_potential_rental_income) {
    items.push({
      label: t('potentialRentalIncomeResidential'),
      value: formatIncome(
        property.residential_potential_rental_income,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.commercial_yearly_rental_income) {
    items.push({
      label: t('actualRentalIncomeCommercial'),
      value: formatIncome(
        property.commercial_yearly_rental_income,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.commercial_potential_rental_income) {
    items.push({
      label: t('potentialRentalIncomeCommercial'),
      value: formatIncome(
        property.commercial_potential_rental_income,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.parking_yearly_rental_income) {
    items.push({
      label: t('actualRentalIncomeParking'),
      value: formatIncome(
        property.parking_yearly_rental_income,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.parking_potential_rental_income) {
    items.push({
      label: t('potentialRentalIncomeParking'),
      value: formatIncome(
        property.parking_potential_rental_income,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.charges) {
    items.push({
      label: t('charges'),
      value: formatIncome(property.charges, currency, locale),
      showByDefault: false,
    });
  }

  if (property?.renovation_fund) {
    items.push({
      label: t('renovationFund'),
      value: formatIncome(property.renovation_fund, currency, locale),
      showByDefault: false,
    });
  }

  return items;
};

export const mapEnergyExtendedItems = (
  property: Property,
  t: Translate,
  locale: IntlLocale,
) => {
  const items: PropertyItem[] = [];
  const currency = getCurrencyByCountryCode(property?.country_code ?? 'CH');

  if (property?.energy_reference_year) {
    items.push({
      label: t('estimateRefYear'),
      value: `${property.energy_reference_year}`,
      showByDefault: false,
    });
  }

  if (property?.energy_inspection_date) {
    items.push({
      label: t('inspectionDate'),
      value: new Date(property.energy_inspection_date).toLocaleDateString(),
      showByDefault: false,
    });
  }

  if (property?.estimated_lower_energy_cost) {
    items.push({
      label: t('minEstimatedCostYear'),
      value: formatIncome(
        property.estimated_lower_energy_cost,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.estimated_higher_energy_cost) {
    items.push({
      label: t('maxEstimatedCostYear'),
      value: formatIncome(
        property.estimated_higher_energy_cost,
        currency,
        locale,
      ),
      showByDefault: false,
    });
  }

  if (property?.energy_certificate_version) {
    items.push({
      label: t('certificateVersion'),
      value: property.energy_certificate_version,
      showByDefault: false,
    });
  }

  return items;
};

export const mapRentalStatusItems = (property: Property, t: Translate) => {
  const items: PropertyItem[] = [];

  if (property?.is_residential_fully_rented != null) {
    items.push({
      label: t('fullyRentedResidential'),
      value: property.is_residential_fully_rented ? t('Yes') : t('No'),
      showByDefault: false,
    });
  }

  if (property?.is_commercial_fully_rented != null) {
    items.push({
      label: t('fullyRentedCommercial'),
      value: property.is_commercial_fully_rented ? t('Yes') : t('No'),
      showByDefault: false,
    });
  }

  if (property?.income_includes_parking != null) {
    items.push({
      label: t('incomeIncludesParking'),
      value: property.income_includes_parking ? t('Yes') : t('No'),
      showByDefault: false,
    });
  }

  if (property?.is_parking_fully_rented != null) {
    items.push({
      label: t('fullyRentedParking'),
      value: property.is_parking_fully_rented ? t('Yes') : t('No'),
      showByDefault: false,
    });
  }

  return items;
};

export const getEquipmentFeatures = (
  property: Property,
  t: Translate,
): string[] => {
  return getPropertyEquipmentFeatures(property, t);
};
